/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-inferrable-types */
import { AuthorizationTokens, DataManagerComponent, EventManager, getCookie, LoggerUtilsComponent, normalizeId } from '@proteo/core';
import { navigateToUrl } from 'single-spa';
import 'zone.js';
import { CheckInvitationService } from '../../src/services/check-invitation.service';
import { ProcessService } from '../../src/services/process.service';
import { TokenService } from '../../src/services/token.service';
import { MfeCommonConfig, PortalConfig, PortalSharedConfig } from '../models/portal-config';
import { Process } from '../models/process.model';
import { LoginOutputData } from '../models/types';
import { HashService } from '../services/hash.service';
import { MfeInitData } from '../services/mfe-init-data.service';

// Dynamic variables
export const getLanguage = (): string => {
  const location = window.location;
  const urlParams = new URLSearchParams(location.search);
  const language = urlParams.get('language')
    ? urlParams.get('language')
    : sessionStorage.getItem('commonLanguage')
    ? sessionStorage.getItem('commonLanguage')
    : 'es-ES';
  sessionStorage.setItem('commonLanguage', language);

  return language;
};

let ccToken: string;
let portalConfig: PortalConfig;
let commonLanguage = getLanguage();
let cto: string;
let clbEnabled: string;
let org: string;
let commonAuth: AuthorizationTokens = {
  OAUTH: '',
  OIDC: ''
};
let commonProps = '{}';
const mfeInitData = new MfeInitData();
const tokenService = new TokenService();
const COLLABORATOR_MAX_ATTEMPTS = 5;

export const setPortalConfig = (config: PortalConfig): void => {
  portalConfig = config;
};
export const getPortalConfig = (): PortalConfig => {
  return portalConfig;
};
export const setCommonLanguage = (l: string): void => {
  sessionStorage.setItem('commonLanguage', l);
  commonLanguage = l;
};
export const setCommonAuth = (auth: AuthorizationTokens): void => {
  DataManagerComponent.getInstance().setAuthorization(auth);
  commonAuth = auth;
};
export const setCto = (c: string): void => {
  sessionStorage.setItem('cto', c);
  cto = c;
};
export const setClbEnabled = (): void => {
  sessionStorage.setItem('clbEnabled', 'true');
  clbEnabled = 'true';
};
export const setPayEnabled = (): void => {
  sessionStorage.setItem('pay', 'true');
};
export const setClbAttempts = (attempts: number): void => {
  sessionStorage.setItem('clbAttempts', attempts.toString());
};
export const setOrg = (o: string): void => {
  sessionStorage.setItem('org', o);
  org = o;
};
export const setLandingData = (siteKey: string): void => {
  mfeInitData.setLandingInitData({ siteKey });
};
export const setCommonProcessId = (processId: string): void => {
  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] setCommonProcessId: {processId:${processId}}`);
  const commonPropsObj = JSON.parse(commonProps);
  sessionStorage.setItem('processId', processId);
  commonPropsObj.processId = processId;
  commonProps = JSON.stringify(commonPropsObj);
};
export const getCommonLanguage = (): string => {
  return commonLanguage;
};
export const getCommonAuth = (): { OAUTH: string; OIDC: string } => {
  return commonAuth;
};

export const getCto = (): string => {
  if (cto !== undefined && cto !== null) {
    return cto;
  } else {
    return sessionStorage.getItem('cto');
  }
};
export const getClbEnabled = (): string => {
  if (clbEnabled) return '';
  const item = sessionStorage.getItem('clbEnabled');
  return item;
};
export const getPayEnabled = (): boolean => {
  return !!sessionStorage.getItem('pay');
};
export const getOrg = (): string => {
  if (org !== undefined && org !== null) {
    return org;
  } else {
    return sessionStorage.getItem('org');
  }
};

export const getCommonProcessId = (): string => {
  const commonPropsObj = JSON.parse(commonProps);
  return commonPropsObj.processId;
};

const processService = new ProcessService();
const checkInvitationService = new CheckInvitationService();

/**
 * Get common config for a concrete micro frontend
 * @returns common configuration
 */
const getCommonMfeConfig = (): MfeCommonConfig => {
  const config = { basePath: portalConfig.CUSTOM_CONFIG.PORTAL_BASE_PATH, language: commonLanguage, auth: commonAuth, customProp: commonProps };
  LoggerUtilsComponent.getInstance().debug('[portal-services.ts] obtaining common configuration:', config);
  return config;
};

/**
 * Get custom config for a concrete micro frontend
 * @param mfeName micro frontend name
 * @returns custom configuration
 */
const getCustomMfeConfig = (mfeName: string): Promise<unknown> => {
  let config;
  const name = normalizeId(mfeName);

  switch (name) {
    case 'LANDING':
      config = mfeInitData.getLandingInitData();
      break;
    case 'RESULTS_MESSAGES':
      config = mfeInitData.getResultsMessagesInitData();
      break;
    case 'MULTIHOLDER_SELECTOR':
      config = mfeInitData.getMultiholderSelectorInitData();
      break;
    case 'MULTIHOLDER_INVITATION':
      config = mfeInitData.getMultiholderInvitationInitData();
      break;
    case 'NEW_ENROLLMENT':
      config = mfeInitData.getEnrollmentInitData();
      break;
    case 'PERSONAL_DATA':
      config = mfeInitData.getPersonalDataInitData();
      break;
    case 'OTP':
      config = mfeInitData.getOtpInitData();
      break;
    case 'ASSOCIATED_BANKING':
      config = mfeInitData.getAssociatedBankingInitData();
      break;
    case 'ASSOCIATED_COLLABORATOR':
      config = mfeInitData.getAssociatedCollaboratorInitData();
      break;
    case 'PREVIOUS_INFO':
      config = mfeInitData.getPreviousInfoInitData();
      break;
    case 'NATIONALITY':
      config = mfeInitData.getNationalityInitData();
      break;
    case 'IDV_IDENTIFICATION':
      config = mfeInitData.getIdvIdentificationInitData();
      break;
    case 'IDENTITY_CHECK':
      config = {
        externalData: mfeInitData.getIdentityCheckInitData().externalData,
        numCheck: mfeInitData.getIdentityCheckInitData().numCheck,
        customer: mfeInitData.getIdentityCheckInitData().customer
      };
      break;
    case 'RESIDENCE_CERTIFICATE':
    case 'ONBOARDING_KYC':
      config = mfeInitData.getOnboardingKYCInitData();
      break;
    case 'ADDRESS_CONFIRMATION':
      config = mfeInitData.getAddressConfirmationInitData();
      break;
    case 'CONTRACT_SIGNING':
      config = mfeInitData.getContractSigningInitData();
      break;
    case 'CHECK_IDV':
      config = mfeInitData.getCheckIdvInitData();
      break;
    case 'LOGIN':
      config = {
        ...mfeInitData.getLoginInitData(),
        ...mfeInitData.getLoginData(portalConfig)
      };
      break;
    case 'MANUAL_RECOVERY_LOGIN':
      config = mfeInitData.getLoginData(portalConfig);
      break;
    case 'RESTORE_PASSWORD':
      tokenService
        .getToken()
        .then((token) => {
          config = { ccToken: token };
        })
        .catch((error) => {
          LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] error obtaining custom configuration for ${name}:`, error);
        });
      break;
    default:
      config = {};
      break;
  }

  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] obtaining custom configuration for ${name}:`, config);
  return config;
};

/**
 * Returns portal shared config for a concrete micro frontend
 * @param mfeName micro frontend name
 * @returns shared config
 */
export const getSharedConfig = (mfeName: string): PortalSharedConfig => {
  LoggerUtilsComponent.getInstance().debug('[portal-services.ts] obtaining shared configuration for:', mfeName);
  return {
    portalConfig: portalConfig,
    commonMfeConfig: getCommonMfeConfig(),
    customMfeConfig: getCustomMfeConfig(mfeName)
  };
};

/**
 *
 * @param data data provided into the event
 */
export const navigateToError = (data: unknown): void => {
  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] navigateTo: {data:${JSON.stringify(data)}}`);
  let url = 'results-messages/error';
  // If base path is empty, we should add initial slash to navigate
  url = '/' + portalConfig.CUSTOM_CONFIG.PORTAL_BASE_PATH + url;
  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] navigateTo: {url:${url}}`);
  mfeInitData.setResultsMessagesInitData(data);
  navigateToUrl(url);
};

export const setRecoveryProcessNextStage = async (data: any, processData: Process): Promise<string> => {
  setCommonProcessId(processData.processId);
  let url: string;
  if (
    sessionStorage.getItem('invitationId') ||
    !(getSharedConfig('LANDING').portalConfig as any).HOLDER_IS_ENABLED ||
    ((getSharedConfig('LANDING').portalConfig as any).HOLDER_IS_ENABLED && processData.processId.length > 0) ||
    sessionStorage.getItem('currentOwner') === '2' ||
    sessionStorage.getItem('loggedFrom') === 'enrollment'
  ) {
    switch (processData.nextStage) {
      case 'CONSIDER':
        EventManager.getInstance().publish('ERROR', { error: '2' });
        break;
      case 'CONSIDER1':
        EventManager.getInstance().publish('ERROR', { error: '3' });
        break;
      case 'REFUSED':
      case 'CONSIDER2':
        EventManager.getInstance().publish('ERROR', { error: '1' });
        break;
      case 'CUSTOMERDATA':
      case 'OTP':
      case 'CANCELLED':
        mfeInitData.setPersonalDataInitData(data as LoginOutputData);
        url = 'personal-data';
        break;
      case 'ASSOCIATED':
        mfeInitData.setAssociatedBankingInitData(data, true);
        url = 'associated-banking';
        break;
      case 'COLLABORATOR':
        mfeInitData.setAssociatedCollaboratorInitData(data, true);
        url = 'associated-collaborator';
        break;
      case 'INFOPREVIA':
        mfeInitData.setPreviousInfoInitData(processData, true);
        url = 'previous-info';
        break;
      case 'COUNTRYDATA':
        mfeInitData.setNationalityInitData(processData, true);
        url = 'nationality';
        break;
      case 'BIOMETRY':
        mfeInitData.setIdvIdentificationInitData(processData, false, false, true);
        url = 'idv-identification';
        break;
      case 'IDV':
        mfeInitData.setIdvIdentificationInitData(processData, true, false, true);
        url = 'idv-identification';
        break;
      case 'IDVPASS':
        sessionStorage.setItem('passportSelected', 'true');
        mfeInitData.setIdvIdentificationInitData(processData, true, false, true);
        url = 'idv-identification/identification-info-page';
        break;
      case 'RESIDENCECARD':
        mfeInitData.setOnboardingKYCInitData(processData, true);
        url = 'residence-certificate';
        break;
      case 'KYC':
        mfeInitData.setOnboardingKYCInitData(processData, true);
        url = 'kyc';
        break;
      case 'ADDRESS':
        mfeInitData.setAddressConfirmationInitData({ outputData: processData, dataFromRecovery: true });
        url = 'address-confirmation';
        break;
      case 'CONFIRM_NAME':
        mfeInitData.setAddressConfirmationInitData({
          outputData: processData,
          dataFromRecovery: true,
          showConfirmDataGenericErrorPage: false,
          confirmData: true
        });
        url = 'address-confirmation/confirm-data';
        break;
      case 'IDV_CHECK':
        mfeInitData.setIdentityCheckInitData(processData, 2, true);
        url = 'identity-check';
        break;
      case 'CONTRACT':
        mfeInitData.setContractSigningInitData(processData);
        url = 'contract-signing';
        break;
      case 'SIGN':
        mfeInitData.setContractSigningInitData(processData, false, true);
        url = 'contract-signing';
        break;
      case 'SIGNED':
        if (processData.numOwners === 1) {
          mfeInitData.setContractSigningInitData(processData, true);
          url = 'contract-signing/complete-onboarding';
        } else {
          mfeInitData.setMultiholderInvitationInitData(processData);
          url = 'multiholder-invitation/multiholder-end';
        }
        break;
      case 'ADDOWNER':
        mfeInitData.setMultiholderInvitationInitData(processData);
        url = 'multiholder-invitation';
        break;
    }
  } else {
    sessionStorage.setItem('multiholderRecovery', 'true');
    mfeInitData.setMultiholderSelectorInitData(processData, true);
    url = 'multiholder-selector';
  }
  return url;
};

export const getError = (nextStage: string): Record<string, string> => {
  const error = { error: '0' };
  switch (nextStage) {
    case 'OFFICE':
      error.error = '14';
      break;
    case 'REFUSED':
      error.error = '1';
      break;
    case 'STOP':
      error.error = '2';
      break;
    case 'KO':
      error.error = '3';
      break;
  }

  return error;
};

/**
 * Navigate to micro frontend from portal
 * @param eventName event name (ex. FINISH.LANDING)
 * @param data data provided into the event
 */
export const navigateTo = async (eventName: string, data: unknown): Promise<void> => {
  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] navigateTo: {eventName:${eventName}, data:${JSON.stringify(data)}}`);
  let url;
  const mfeName = eventName.split('.')[1];

  if ((data as any).errorCode) {
    const processData: Process = await processService.getProcess('EXPANSIONDIGITAL');
    const currentOwner = sessionStorage.getItem('invitationId') ? 2 : processData.currentOwner;
    const numOwners = currentOwner === 2 || sessionStorage.getItem('isSecondHolderSelected') === 'true' ? 2 : processData.numOwners;
    // save owner
    sessionStorage.setItem('currentOwner', `${currentOwner}`);
    sessionStorage.setItem('numOwners', `${numOwners}`);

    url = await setRecoveryProcessNextStage(data, processData);
  } else {
    const litPreviousInfo = 'previous-info';
    switch (mfeName) {
      case 'LANDING':
        sessionStorage.removeItem('tagSended');
        // Go to multiholder_selector or enrollment
        const nextUrl = (data as any).nextUrl;
        if (nextUrl === 'enrollment') {
          mfeInitData.setEnrollmentInitData(data);
        } else if (nextUrl === 'multiholder-selector') {
          mfeInitData.setMultiholderSelectorInitData(data);
        }

        url = nextUrl;
        break;
      case 'MULTIHOLDER_SELECTOR':
        // Go to enrollment or personal-data
        if ((data as any).dataFromProcess) {
          mfeInitData.setPersonalDataInitData((data as any).dataFromProcess as LoginOutputData);
          url = 'personal-data';
        } else {
          mfeInitData.setEnrollmentInitData(data);
          url = 'enrollment';
        }
        break;
      case 'NEW_ENROLLMENT':
        sessionStorage.setItem('dni', (data as any).userIdentifier);
        sessionStorage.setItem('loggedFrom', 'enrollment');
        sessionStorage.setItem('commonLanguage', commonLanguage);
        mfeInitData.setLoginInitData(data, portalConfig);
        url = 'login';
        break;
      case 'LOGIN':
        sessionStorage.removeItem('identificationAuth');
        sessionStorage.removeItem('skipBiometricPage');
        sessionStorage.removeItem('showVerificationErrorPage');
        sessionStorage.removeItem('basePath');
        sessionStorage.removeItem('showConfirmDataGenericErrorPage');
        sessionStorage.removeItem('processId');
        sessionStorage.removeItem('cacheData');
        sessionStorage.removeItem('customerInfo');
        sessionStorage.removeItem('documentsPrevInfo');
        sessionStorage.removeItem('currentStage');
        sessionStorage.removeItem('disabledPromotionalCode');
        sessionStorage.removeItem('promotionalCode');
        sessionStorage.removeItem('isFromNationality');
        sessionStorage.removeItem('fiscalAddress');
        sessionStorage.removeItem('postalAddress');
        sessionStorage.removeItem('documentsKyc');
        sessionStorage.removeItem('recoveryKyc');
        sessionStorage.removeItem('confirmName');
        sessionStorage.removeItem('hasGoneThroughSelector');
        sessionStorage.removeItem('confirmName');
        sessionStorage.removeItem('contractIsGenerate');
        sessionStorage.removeItem('isNie');
        sessionStorage.removeItem('passportSelected');
        sessionStorage.removeItem('selfEmployed');
        sessionStorage.removeItem('clbAttempts');

        const hashService = new HashService();
        const dniHash = hashService.md5(sessionStorage.getItem('dni'));

        const nieRegex = /^[xyzXYZ]\d{5,8}[A-Za-z]$/;
        nieRegex.test(sessionStorage.getItem('dni')) ? sessionStorage.setItem('isNie', 'true') : sessionStorage.setItem('isNie', 'false');

        sessionStorage.setItem('bscid', dniHash);
        const auth = { OAUTH: (data as LoginOutputData).accessToken, OIDC: (data as LoginOutputData).oidcToken };
        sessionStorage.setItem('identificationAuth', JSON.stringify(auth));
        setCommonAuth(auth);
        setCommonLanguage(sessionStorage.getItem('commonLanguage'));
        LoggerUtilsComponent.getInstance().debug('[portal-services.ts] common authentication after new enrollment', commonAuth);
        setCommonProcessId('N/A');

        const headers: Record<string, string> = {
          'X-User-Agent': navigator.userAgent,
          // 'X-Originating-IP': sessionStorage.getItem('ip'),
          'X-Device-Cookie': getCookie('DeviceTokenCookie'),
          'X-Device-Fingerprint': sessionStorage.getItem('deviceprint')
        };

        const payValue = getPayEnabled() ? 'true' : 'false';
        processService.postProcessLog('LOGIN', 'LOGIN', 'INFO', payValue, 'DEVICE_PROPERTIES', headers);

        if (getCto() !== '310001' && getCto() !== null) {
          processService.postProcessLog('COLECTIVO', 'LOGIN', 'INFO', getCto());
        }

        if (getOrg() !== null) {
          processService.postProcessLog('OFICINA', 'LOGIN', 'INFO', getOrg());
        }

        if (getClbEnabled() === 'true') {
          processService.postProcessLog('COLABORADOR', 'LOGIN', 'INFO', 'CLB');
        }

        const processData: Process = await processService.getProcess('EXPANSIONDIGITAL');

        if (processData.additionalData.length > 0) {
          processData.additionalData.forEach((el) => {
            if (el.code === 'isPassport' && el.value === 'true') {
              sessionStorage.setItem('passportSelected', 'true');
            }
            if (el.code === 'CTO') {
              setCto(el.value);
            }
            if (el.clbEnabled) {
              setClbEnabled();
            }
            if (el.code === 'attemptsCollaboratorValidation') {
              // Back no cuenta intentos pendientes sino realizados
              const attempts = COLLABORATOR_MAX_ATTEMPTS - parseInt(el.value);
              setClbAttempts(attempts);
            }

            const contactInfo = {
              contactInfoPage: {
                consentTreatment: '',
                consentComerce: ''
              }
            };
            if (el.code === 'ct' && el.value === 'true') {
              contactInfo.contactInfoPage.consentTreatment = 'accepted';
            }
            if (el.code === 'cc' && el.value === 'true') {
              contactInfo.contactInfoPage.consentComerce = 'accepted';
            }
            sessionStorage.setItem('cacheData', JSON.stringify(contactInfo));
          });
        }

        //save in session the flag to true when self-employed
        if (processData.indSelfEmployed) {
          sessionStorage.setItem('selfEmployed', 'true');
        }

        // save owner
        const currentOwner = sessionStorage.getItem('invitationId') ? 2 : processData.currentOwner;
        const numOwners = currentOwner === 2 || sessionStorage.getItem('isSecondHolderSelected') === 'true' ? 2 : processData.numOwners;

        sessionStorage.setItem('currentOwner', `${currentOwner}`);
        sessionStorage.setItem('numOwners', `${numOwners}`);
        const loggedFrom = sessionStorage.getItem('loggedFrom');
        const loader = document.getElementById('loader');
        if (loader) loader.remove();

        const error = getError(processData.nextStage);

        if (error.error !== '0') {
          mfeInitData.setResultsMessagesInitData(error);
          url = 'results-messages/error';
        } else {
          // TODO: temp erase
          if (loggedFrom === 'enrollment') {
            const invitationId = sessionStorage.getItem('invitationId');
            if (invitationId != null) {
              await checkInvitationService.acceptedInvitation(invitationId);
            }
            url = await setRecoveryProcessNextStage(data, processData);
          } else if (loggedFrom === 'recovery') {
            url = await setRecoveryProcessNextStage(data, processData);
          }
        }
        break;
      case 'MANUAL_RECOVERY_LOGIN':
        if ((data as any).restorePassword) {
          url = 'restore-password';
        } else {
          LoggerUtilsComponent.getInstance().debug('data.redirectUrl:', (data as any).redirectUrl);
          url = 'login?' + (data as any).redirectUrl.split('login?')[1];
        }
        break;
      case 'PERSONAL_DATA':
        mfeInitData.setOtpInitData(data);
        url = 'otp';
        break;
      case 'OTP':
        if (getCto() === '310001') {
          mfeInitData.setAssociatedBankingInitData(data);
          url = 'associated-banking';
        } else if (getClbEnabled() === 'true') {
          mfeInitData.setAssociatedCollaboratorInitData(data);
          url = 'associated-collaborator';
        } else {
          mfeInitData.setPreviousInfoInitData(data);
          url = litPreviousInfo;
        }
        break;
      case 'ASSOCIATED_BANKING':
        mfeInitData.setPreviousInfoInitData(data);
        url = litPreviousInfo;
        break;
      case 'ASSOCIATED_COLLABORATOR':
        mfeInitData.setPreviousInfoInitData(data);
        url = litPreviousInfo;
        break;
      case 'PREVIOUS_INFO':
        mfeInitData.setNationalityInitData(data);
        url = 'nationality';
        break;
      case 'NATIONALITY':
        mfeInitData.setIdvIdentificationInitData(data, false, false);
        url = 'idv-identification';
        break;
      case 'BIOMETRY':
        mfeInitData.setIdvIdentificationInitData(data, true, false);
        url = 'idv-identification';
        break;
      case 'IDV_IDENTIFICATION':
        mfeInitData.setIdentityCheckInitData(data, 1);
        url = 'identity-check';
        break;
      case 'RESIDENCE_CERTIFICATE':
        url = 'kyc';
        break;
      case 'ONBOARDING_KYC':
        mfeInitData.setAddressConfirmationInitData({ outputData: data });
        url = 'address-confirmation';
        break;
      case 'ADDRESS_CONFIRMATION':
        if ((data as any).showVerificationErrorPage) {
          await mfeInitData.setIdvIdentificationInitData(data, false, true);
          url = 'idv-identification';
        } else if ((data as any).showContractPage) {
          mfeInitData.setContractSigningInitData(data);
          url = 'contract-signing';
        } else {
          mfeInitData.setIdentityCheckInitData(data, 2);
          url = 'identity-check';
        }
        break;
      case 'IDENTITY_CHECK':
        const jsonData = JSON.parse((data as any).jsonData);
        const numCheck = jsonData.numCheck;

        if (numCheck === 1) {
          mfeInitData.setOnboardingKYCInitData(data);
          if (sessionStorage.getItem('passportSelected')) {
            url = 'residence-certificate';
          } else {
            url = 'kyc';
          }
        } else {
          mfeInitData.setCheckIdvInitData(data);
          url = 'check-idv';
        }
        break;
      case 'CHECK_IDV':
        if ((data as any).showConfirmDataErrorPage) {
          mfeInitData.setAddressConfirmationInitData({ outputData: data, showConfirmDataGenericErrorPage: false, confirmData: true });
          url = 'address-confirmation/confirm-data';
        }
        if ((data as any).showConfirmDataGenericErrorPage) {
          await mfeInitData.setAddressConfirmationInitData({ outputData: data, showConfirmDataGenericErrorPage: true });
          url = 'address-confirmation';
        }
        if ((data as any).showContract) {
          mfeInitData.setContractSigningInitData(data, false);
          url = 'contract-signing';
        }
        break;
      case 'RESULTS_MESSAGES':
        await mfeInitData.setIdvIdentificationInitData(data, false, false);
        url = 'idv-identification/identification-info-page';
        break;
      case 'CONTRACT_SIGNING':
        await mfeInitData.setMultiholderInvitationInitData(data);
        if (sessionStorage.getItem('isSecondHolderSelected') === 'true') {
          url = 'multiholder-invitation';
        } else {
          url = 'multiholder-invitation/multiholder-account-purpose';
        }
        if ((data as any).relatedOwner) {
          await mfeInitData.setMultiholderInvitationInitData(data);
          url = 'multiholder-invitation/multiholder-end';
        }
        break;
      case 'RESTORE_PASSWORD':
        url = 'recovery-login';
        break;
    }
  }
  // If base path is empty, we should add initial slash to navigate
  url = '/' + portalConfig.CUSTOM_CONFIG.PORTAL_BASE_PATH + url;
  LoggerUtilsComponent.getInstance().debug(`[portal-services.ts] navigateTo: {url:${url}}`);

  navigateToUrl(url);
};
